// Copy for the AWS practice page. Ported from the standalone AWS microsite.
// Internal planning fields (leadWith, note, codename) are deliberately not
// carried over; they were never meant to reach a public page or bundle.

// Story lead images.
import lokistatStoryImage from '../assets/images/story-sports-tech.jpg';
import qrideStoryImage from '../assets/images/story-qride.jpg';
import solopreneurStoryImage from '../assets/images/story-solopreneur.jpg';

export const AWS_CALENDLY_URL = 'https://calendly.com/flamyjg-jaggulasconsulting';

export const awsServices = [
  {
    id: 'aws-cloud-consulting-architecture',
    name: 'Cloud Consulting & Architecture',
    points: [
      'Architecture design, security enhancement, scalability strategy',
      'Follows AWS Well-Architected best practices (formal review pending)',
    ],
  },
  {
    id: 'aws-migration-modernization',
    name: 'Migration & Modernization',
    points: [
      'Seamless transitions with minimal disruption',
      'Performance optimization and cost reduction framing',
    ],
  },
  {
    id: 'aws-managed-services',
    name: 'Managed Services',
    points: [
      'Ongoing monitoring, security, optimization, support, vendor onboarding, centralized governance',
      'Proof point: multi-tenant setup for an Africa-based sports-tech client using AWS Organizations, Control Tower, and Service Control Policies to isolate competing teams',
      'Transit Gateway network isolation plus centralized, immutable logging',
    ],
  },
  {
    id: 'aws-cost-optimization',
    name: 'Cost Optimization',
    points: [
      'Right-sizing and strategic cost management',
      'Measurable savings, reported against your current spend',
    ],
  },
  {
    id: 'aws-web-backend-development',
    name: 'Web & Backend Development',
    points: [
      'Cloud-native web apps, APIs, databases, API Gateway management',
      'Proof point: Qride, a real-time ride-hailing marketplace built on Amazon ECS Fargate auto-scaling with live IoT vehicle tracking and driver KYC verification',
    ],
  },
  {
    id: 'aws-ai-agents-automation',
    name: 'AI Agents & Automation',
    points: [
      'AI voice bots, chatbots, business process automation, process orchestration',
      'Tied to the healthcare AI-readiness narrative',
      'Proof point: Solopreneur, an AgentCore Supervisor orchestrating specialized agents (calendar, email, meetings, tasks, CRM) on Amazon Bedrock, with a human-approval step before any action is sent',
    ],
  },
];

// Narrative case studies.
//
// Every prose section (problem, about, solution, value, impact) is an array of
// blocks so the stories can differ in shape without the renderer growing a
// special case per story. A block is one of:
//
//   'text'                          → paragraph
//   { lead: 'text' }                → emphasised opening line
//   { heading: 'text' }             → sub-heading inside the section
//   { points: [...] }               → bullet list; an item may be
//                                     { text, sub: [...] } for one nested level
//   { terms: [{ term, detail }] }   → labelled definition list
//   { flow: [...] }                 → arrow-separated progression
//
// `titles` overrides the default section headings. `engagement` renders only
// when `confirmed` is true; the figures in the source document are still
// marked as estimates.
export const awsCaseStudies = [
  {
    id: 'lokistat',
    order: '01',
    discipline: 'Cloud platform engineering & AI transformation',
    name: 'Lokistat',
    kicker: 'From cloud migration to AI transformation.',
    teaser:
      'A sports technology company that moved to AWS for a secure, scalable foundation, and is now building toward advanced analytics and AI on top of it.',
    titles: { problem: 'The challenge', solution: 'The solution' },
    image: {
      src: lokistatStoryImage,
      alt:
        'A football analytics operations room. Wall screens and a large touch table show player profiles, fitness and fatigue metrics, tactical deployment maps, a predictive model diagram and a match win-probability forecast.',
    },
    problem: [
      {
        points: [
          'Lokistat is a sports technology company using data to improve performance and decision-making.',
          'Its existing infrastructure needed to support future growth.',
          {
            text: 'Key requirements included:',
            sub: [
              'Better scalability',
              'Stronger security and resilience',
              'Improved infrastructure visibility',
              'Support for advanced analytics and AI',
              'A trusted long-term technology partner',
            ],
          },
        ],
      },
    ],
    about: [
      {
        points: [
          'Lokistat engaged Jaggulas Consulting to migrate its infrastructure to AWS.',
          'The goal was to build a secure, scalable and reliable cloud foundation.',
          'The engagement has since grown into a broader technology partnership.',
        ],
      },
    ],
    solution: [
      { heading: '1. AWS cloud migration' },
      {
        points: [
          "Migrated Lokistat's existing infrastructure to AWS.",
          'Built a more scalable and secure cloud environment.',
          'Improved flexibility, reliability and infrastructure management.',
          'Created a foundation for future technology growth.',
        ],
      },
      { heading: '2. Managed cloud services' },
      {
        points: [
          "Jaggulas Consulting continues to manage Lokistat's AWS environment.",
          {
            text: 'Ongoing support includes:',
            sub: [
              'Monitoring',
              'Security',
              'Optimisation',
              'Infrastructure management',
              'Operational support',
            ],
          },
        ],
      },
      { heading: '3. Advanced analytics & AI' },
      {
        points: [
          'The successful migration created the foundation for the next phase.',
          {
            text: "Jaggulas Consulting is now supporting Lokistat's exploration of:",
            sub: [
              'Advanced analytics',
              'Predictive modelling',
              'Machine learning',
              'Scalable data infrastructure',
            ],
          },
          'The objective is to turn sports data into actionable insights and intelligent predictions.',
        ],
      },
    ],
    value: [
      {
        terms: [
          {
            term: 'Scalable foundation',
            detail: 'AWS provides the flexibility to support future growth.',
          },
          {
            term: 'Improved operations',
            detail: 'Better visibility, management and reliability.',
          },
          {
            term: 'Ongoing support',
            detail:
              'Lokistat can focus on its core business while Jaggulas manages the cloud environment.',
          },
          {
            term: 'AI-ready infrastructure',
            detail: 'The AWS platform supports advanced analytics and machine learning.',
          },
          {
            term: 'Long-term partnership',
            detail:
              'One technology partner supports Lokistat from infrastructure to innovation.',
          },
        ],
      },
    ],
    impact: [
      {
        points: [
          'The engagement started with a cloud migration challenge.',
          'Successful delivery built trust between Lokistat and Jaggulas Consulting.',
          'The relationship expanded from cloud migration to managed services and AI transformation.',
        ],
      },
      { flow: ['Cloud migration', 'Managed services', 'Digital transformation', 'AI innovation'] },
      'For Lokistat, AWS is not just infrastructure. It is the foundation for what comes next.',
    ],
    featured: true,
  },
  {
    id: 'qride',
    order: '02',
    discipline: 'Cloud-native application development',
    name: 'Qride',
    kicker: 'Scalable, secure ride-hailing platform.',
    teaser:
      'A cloud-native ride-hailing platform built to handle changing demand, real-time vehicle tracking, driver verification and enterprise-grade security.',
    titles: { problem: 'The challenge', solution: 'The solution' },
    image: {
      src: qrideStoryImage,
      alt:
        'The Qride mobile app shown over a map of central Cape Town, with pickup location, ride classes and fares on the phone, and callouts for driver verification, live ride tracking, pickup location and an emergency button.',
    },
    problem: [
      {
        points: [
          'Ride-hailing demand can change rapidly due to commuting, weather and major events.',
          'Qride needed infrastructure that could scale with demand.',
          {
            text: 'Key requirements included:',
            sub: [
              'Automatic scaling during demand surges',
              'Secure driver identity verification',
              'Real-time vehicle tracking',
              'Protection of customer and location data',
              'Reliable and cost-efficient operations',
            ],
          },
        ],
      },
    ],
    about: [
      {
        points: [
          'Qride is a real-time ride-hailing and mobility platform.',
          {
            text: 'It connects riders with drivers and manages:',
            sub: [
              'Driver onboarding',
              'Ride matching',
              'Live tracking',
              'Payments and settlements',
              'Notifications',
            ],
          },
          'Jaggulas Consulting designed, developed and deployed the platform on AWS.',
        ],
      },
    ],
    solution: [
      { heading: '1. Scalable microservices architecture' },
      {
        points: [
          {
            text: 'Built Qride using independent services for:',
            sub: [
              'Ride matching',
              'Vehicle tracking',
              'Payments',
              'Driver onboarding',
              'Notifications',
            ],
          },
          'Each service can be updated and scaled independently.',
          'AWS ECS Fargate provides serverless container-based compute.',
          'Capacity automatically scales based on demand.',
        ],
      },
      { heading: '2. Real-time tracking' },
      {
        points: [
          'Live vehicle data is continuously captured and processed.',
          'Riders can see vehicle locations in real time.',
          {
            text: 'The data also supports:',
            sub: [
              'Accurate arrival estimates',
              'Trip history',
              'Dispute resolution',
              'Operations monitoring',
            ],
          },
        ],
      },
      { heading: '3. Driver KYC and identity verification' },
      {
        points: [
          'Driver identity verification is built into the onboarding process.',
          'Drivers must complete verification before accepting rides.',
          'This creates a stronger foundation for platform safety and trust.',
        ],
      },
      { heading: '4. Layered AWS security' },
      {
        points: [
          'AWS WAF protects against common web attacks.',
          'AWS Shield provides protection against DDoS attacks.',
          'Amazon GuardDuty continuously monitors for suspicious activity.',
          'AWS KMS manages encryption keys and protects data at rest.',
        ],
      },
    ],
    value: [
      {
        terms: [
          { term: 'Scalable', detail: 'Infrastructure automatically responds to changes in demand.' },
          { term: 'Cost-efficient', detail: 'Resources scale with actual usage instead of peak capacity.' },
          { term: 'Reliable', detail: 'The platform can handle demand surges with less manual intervention.' },
          { term: 'Safer', detail: 'Driver verification and live tracking are built into the customer journey.' },
          { term: 'Better visibility', detail: 'Real-time data supports operations and customer support.' },
          { term: 'Secure', detail: 'Multiple AWS security services protect applications and data.' },
          { term: 'Agile', detail: 'Independent services allow features to be developed and released faster.' },
        ],
      },
    ],
    impact: [
      'Qride now has a scalable, secure and real-time technology platform designed for the demands of a growing mobility marketplace.',
      { flow: ['Scalable infrastructure', 'Real-time mobility', 'Built-in security', 'Faster innovation'] },
      'Jaggulas Consulting helped Qride build a scalable cloud foundation to support its growth.',
    ],
    featured: false,
  },
  {
    id: 'solopreneur',
    order: '03',
    discipline: 'AI agents & intelligent automation',
    name: 'Solopreneur',
    kicker: 'AI-powered business operating system.',
    teaser:
      'An AI-native platform connecting CRM, email, meetings, calendar, tasks and documents through specialised AI agents with human approval built into every outbound action.',
    titles: { problem: 'The challenge', solution: 'The solution' },
    image: {
      src: solopreneurStoryImage,
      alt:
        'An illustration of a small-business owner at a laptop. A single plain-language request is decomposed by an AgentCore Supervisor and fanned out to specialised CRM, calendar, email, meetings, tasks and documents agents, which return a queue of drafted items waiting behind an approve-and-send gate.',
    },
    problem: [
      {
        points: [
          'Small businesses often rely on multiple disconnected tools for CRM, email, calendars, meetings, tasks and documents.',
          'Moving information between these tools takes time and creates inefficiencies.',
          'Generic AI assistants often lack business context or provide too much autonomy.',
          {
            text: 'Solopreneur needed an AI solution that was:',
            sub: ['Context-aware', 'Useful', 'Secure', 'Controlled by the user', 'Easy to use'],
          },
        ],
      },
    ],
    about: [
      {
        points: [
          'Solopreneur is an AI-native operating system for solopreneurs and small businesses.',
          'It brings CRM, calendar, email, meetings, tasks and documents into one conversational interface.',
          'Users can manage business activities using simple natural-language requests.',
        ],
      },
    ],
    solution: [
      { heading: '1. AI supervisor' },
      {
        points: [
          'An AgentCore Supervisor running on Amazon Bedrock understands each user request.',
          'It breaks the request into individual tasks.',
          'It determines the sequence and assigns tasks to the right specialised agent.',
        ],
      },
      { heading: '2. Specialised AI agents' },
      {
        points: [
          {
            text: 'Dedicated agents handle specific business functions:',
            sub: [
              'CRM and relationship history',
              'Meetings and notes',
              'Email and communication',
              'Calendar and scheduling',
            ],
          },
          'Each agent focuses on a specific task for greater reliability and accuracy.',
        ],
      },
      { heading: '3. Human approval' },
      {
        points: [
          'AI prepares actions but does not execute sensitive outbound actions automatically.',
          'Emails, calendar invites and CRM updates are presented to the user for approval.',
          'The user remains in control of what is sent or changed.',
        ],
      },
      { heading: '4. Connected business context' },
      {
        points: [
          'Information flows between business functions.',
          'Meeting discussions can update CRM records.',
          'CRM context can inform future communications.',
          'This creates a connected view of the customer and business relationship.',
        ],
      },
    ],
    value: [
      {
        terms: [
          { term: 'Saves time', detail: 'Reduces repetitive administrative work.' },
          {
            term: 'Improves productivity',
            detail: 'Frees business owners to focus on revenue-generating activities.',
          },
          { term: 'Builds trust', detail: 'Human approval is required before outbound actions.' },
          { term: 'Maintains accountability', detail: 'Actions can be reviewed and tracked.' },
          { term: 'Preserves context', detail: 'Business information stays connected across tools.' },
          {
            term: 'Scales easily',
            detail: 'New capabilities can be added through specialised AI agents.',
          },
        ],
      },
    ],
    impact: [
      'Solopreneur transforms disconnected business tools into a single AI-powered operating system that combines automation with human control.',
      { flow: ['Connected tools', 'AI agents', 'Human approval', 'Intelligent business operations'] },
      'Jaggulas Consulting is building Solopreneur to make AI practical, controlled and useful for everyday business operations.',
    ],
    featured: false,
  },
];
